import type { ComponentType, SVGProps } from "react";
import { cn } from "../../lib/utils";

type IconType = ComponentType<SVGProps<SVGSVGElement>>;

export interface SegmentItem<V extends string> {
  value: V;
  label: string;
  /** 14px leading icon; primary-tinted when the segment is active. */
  icon?: IconType;
  /** Swapped in for `icon` while active (v3 uses the bold variant). */
  activeIcon?: IconType;
  count?: number;
  /** Colour of the count: primary for "needs attention", muted otherwise. */
  countTone?: "primary" | "muted";
}

export interface SegmentedControlProps<V extends string> {
  items: SegmentItem<V>[];
  value: V;
  onChange: (value: V) => void;
  /** Fully round bar and segments (meeting filters); default is soft-rounded (dashboard). */
  pill?: boolean;
  /** Stretch segments to fill the container. */
  fullWidth?: boolean;
  "aria-label"?: string;
  className?: string;
}

/** Pill/segment switch with a white active segment (Today · This Week; All · Upcoming …). */
export function SegmentedControl<V extends string>({
  items,
  value,
  onChange,
  pill = false,
  fullWidth = false,
  className,
  ...props
}: SegmentedControlProps<V>) {
  return (
    <div
      role="group"
      aria-label={props["aria-label"]}
      className={cn(
        "grid-flow-col gap-1 border border-black/[0.08] bg-surface-3 p-1 text-xs font-semibold",
        fullWidth ? "grid auto-cols-fr" : "inline-grid auto-cols-max",
        pill ? "rounded-full" : "rounded-2xl",
        className
      )}
    >
      {items.map(({ value: v, label, icon, activeIcon, count, countTone = "muted" }) => {
        const active = v === value;
        const Icon = active ? (activeIcon ?? icon) : icon;
        return (
          <button
            key={v}
            type="button"
            aria-pressed={active}
            onClick={() => onChange(v)}
            className={cn(
              "flex cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap px-4 py-1.5 transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40",
              pill ? "rounded-full" : "rounded-xl",
              active
                ? "bg-white font-bold text-ink shadow-xs"
                : "font-medium text-ink-muted hover:bg-white/40 hover:text-ink"
            )}
          >
            {Icon && (
              <Icon
                aria-hidden="true"
                className={cn("size-3.5 shrink-0", active ? "text-primary" : "text-ink-muted")}
              />
            )}
            <span>{label}</span>
            {typeof count === "number" && (
              <span
                className={cn(
                  countTone === "primary" ? "text-primary" : "text-ink-muted"
                )}
              >
                {count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
